import { useQuery } from "@tanstack/react-query";
import { OverviewHub } from "~/components/company/overview/overview-hub";
import type { DocumentsResponse } from "~/components/company/tabs/documents-tab";
import { authHeaders } from "~/lib/auth-headers";
import {
	companyCanonicalUrl,
	companyFromMatches,
	companyNotFoundMeta,
	companySocialMeta,
	useCompanyProfile,
} from "~/lib/company-profile";
import { envConfig } from "~/lib/env-validation";
import { TENDERS_ENABLED } from "~/lib/features";
import { generateMetaTags } from "~/lib/seo";
import { type CompanyTenderStats, fetchCompanyTenderStats } from "~/lib/tenders";
import type { CompanyChangeEvent, RelatedCompaniesResponse } from "~/types/company";
import type { CorporateEventsResponse } from "~/types/corporate";
import type { Route } from "./+types/prehled";

interface CompanyOverviewResponse {
	changes: CompanyChangeEvent[];
	documents: DocumentsResponse;
	contracts: {
		total: number;
		totalValueCzk: number | null;
		supplierCount: number;
		buyerCount: number;
	};
	related: RelatedCompaniesResponse;
	events: CorporateEventsResponse;
	tenderStats: CompanyTenderStats | null;
}

class OverviewFetchError extends Error {
	status: number | null;

	constructor(message: string, status: number | null) {
		super(message);
		this.status = status;
	}
}

async function fetchOverview(ico: string, signal: AbortSignal): Promise<CompanyOverviewResponse> {
	let response: Response;
	try {
		response = await fetch(`${envConfig.apiBaseUrl}/companies/${ico}/overview`, {
			headers: await authHeaders(),
			signal,
		});
	} catch (error) {
		if (error instanceof DOMException && error.name === "AbortError") throw error;
		throw new OverviewFetchError("Nepodařilo se spojit se serverem.", null);
	}

	if (!response.ok) {
		throw new OverviewFetchError("Doplňující přehled se nepodařilo načíst.", response.status);
	}
	const overview = (await response.json()) as Omit<CompanyOverviewResponse, "tenderStats">;
	const tenderStats = TENDERS_ENABLED
		? await fetchCompanyTenderStats(ico, signal).catch(() => null)
		: null;
	return { ...overview, tenderStats };
}

export function meta({ matches }: Route.MetaArgs) {
	const company = companyFromMatches(matches);
	if (!company) return companyNotFoundMeta();

	const title = `${company.name} – IČO ${company.ico}${company.city ? `, ${company.city}` : ""} | FirmIndex`;
	const description = `Profil firmy ${company.name} (IČO: ${company.ico})${company.city ? `, ${company.city}` : ""}. Obchodní rejstřík, finanční údaje, statutární orgány.`;

	return generateMetaTags({
		...companySocialMeta(company),
		title,
		description,
		canonical: companyCanonicalUrl(company),
		noindex: company.adverseDataRestricted,
	});
}

export default function CompanyPrehledRoute() {
	const company = useCompanyProfile();
	const overviewQuery = useQuery({
		queryKey: ["company-overview", company.ico, company.viewerTier],
		queryFn: ({ signal }) => fetchOverview(company.ico, signal),
		staleTime: 5 * 60_000,
		retry: (failureCount, error) =>
			failureCount < 1 &&
			error instanceof OverviewFetchError &&
			(error.status === null || error.status >= 500),
	});

	return (
		<OverviewHub
			company={company}
			overview={overviewQuery.data ?? null}
			overviewStatus={overviewQuery.status}
			onRetry={() => overviewQuery.refetch()}
		/>
	);
}
